<script setup>
import { computed, inject, onMounted, shallowRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProjects } from "~/composables/useProjects";
import PageShell from "~/components/PageShell.vue";
import GlAlert from "~/components/gitlab/GlAlert.vue";
import ProjectsToolbar from "~/components/ProjectsToolbar.vue";
import ProjectsList from "~/components/ProjectsList.vue";
import AddProjectModal from "~/components/AddProjectModal.vue";
import DeleteProjectModal from "~/components/DeleteProjectModal.vue";

const TAB_DEFS = [
  { id: "active", label: "Active", title: "Active projects" },
  {
    id: "inactive",
    label: "Inactive",
    title: "Projects without configuration"
  }
];

const SORT_OPTIONS = [
  { text: "Name", value: "name" },
  { text: "Created date", value: "created_at" },
  { text: "Last status", value: "last_run_status" }
];

const isAdmin = inject("isAdmin", false);

const route = useRoute();
const router = useRouter();

const { projects, pagination, loading, error, fetchProjects, fetchCount, clearError } = useProjects();

/**
 * Derive tab index from URL query param.
 *
 * @param {string|undefined} tabParam
 * @returns {number}
 */
function tabIndexFromQuery(tabParam) {
  const idx = TAB_DEFS.findIndex((t) => t.id === tabParam);
  return idx >= 0 ? idx : 0;
}

// Numeric index — matches GlTabs v-model
const activeTabIndex = shallowRef(tabIndexFromQuery(route.query.tab));
// Derived string ID used for fetch params
const activeTabId = computed(() => TAB_DEFS[activeTabIndex.value]?.id ?? "active");

// Per-tab counts: { active: number|null, inactive: number|null }
const tabCounts = shallowRef({ active: null, inactive: null });

// Keep the active tab's count in sync with the latest pagination result.
watch(pagination, (p) => {
  if (p?.count != null) {
    tabCounts.value = { ...tabCounts.value, [activeTabId.value]: p.count };
  }
});

// Inject counts into tab definitions for the badge.
const tabs = computed(() =>
  TAB_DEFS.map((tab) => ({
    ...tab,
    count: tabCounts.value[tab.id] ?? undefined
  }))
);

const searchQuery = shallowRef(route.query.q ?? "");
const sortBy = shallowRef(route.query.sort ?? "name");
const isAscending = shallowRef(route.query.direction !== "desc");

const showAddModal = shallowRef(false);
const showDeleteModal = shallowRef(false);
const deleteTargetId = shallowRef(null);
const deleteTargetName = shallowRef("");
const pageAlert = shallowRef(null);

/**
 * Build query params object for the URL, omitting defaults.
 *
 * @param {{ tab?: string, q?: string, page?: number }} overrides
 * @returns {Object}
 */
function buildQuery(overrides = {}) {
  const tab = overrides.tab ?? activeTabId.value;
  const q = overrides.q ?? searchQuery.value;
  const page = overrides.page ?? 1;

  const query = {};
  if (tab !== "active") query.tab = tab;
  if (q) query.q = q;
  if (page > 1) query.page = String(page);
  if (sortBy.value !== "name") query.sort = sortBy.value;
  if (!isAscending.value) query.direction = "desc";
  return query;
}

/**
 * Build fetch params including sort fields.
 *
 * @param {{ page?: number }} params
 * @returns {Object}
 */
function buildFetchParams(params = {}) {
  const page = params.page ?? 1;
  return {
    active: activeTabId.value === "active",
    project_name: searchQuery.value || undefined,
    sort_by: sortBy.value,
    sort_direction: isAscending.value ? "asc" : "desc",
    ...(page > 1 ? { page } : {})
  };
}

/**
 * Fetch projects using current state, then update URL to match.
 *
 * @param {{ page?: number }} params
 * @param {{ push?: boolean }} options
 */
function fetchAndUpdateUrl(params = {}, { push = false } = {}) {
  const page = params.page ?? 1;

  fetchProjects(buildFetchParams({ page }));

  const query = buildQuery({ page });
  if (push) {
    router.push({ query });
  } else {
    router.replace({ query });
  }
}

async function fetchInactiveCount() {
  const inactiveCount = await fetchCount({ active: false });
  tabCounts.value = { ...tabCounts.value, inactive: inactiveCount };
}

// On mount, fetch only the inactive count — the active count comes from the
// main fetchProjects call (via the pagination watcher above).
onMounted(() => {
  fetchInactiveCount();

  // Initial fetch based on URL state
  fetchAndUpdateUrl({ page: Number(route.query.page) || 1 });
});

function onTabChange(index) {
  activeTabIndex.value = index;
  searchQuery.value = "";
  fetchAndUpdateUrl({}, { push: true });
}

function onSearch() {
  fetchAndUpdateUrl({}, { push: true });
}

function onPaginate(params) {
  fetchAndUpdateUrl({ page: params.page });
}

function onSortByChange(value) {
  sortBy.value = value;
  fetchAndUpdateUrl({}, { push: true });
}

function onSortDirectionChange(ascending) {
  isAscending.value = ascending;
  fetchAndUpdateUrl({}, { push: true });
}

// React to browser back/forward navigation
watch(
  () => route.query,
  (query) => {
    const newTabIndex = tabIndexFromQuery(query.tab);
    const newSearch = query.q ?? "";
    const newPage = Number(query.page) || 1;
    const newSortBy = query.sort ?? "name";
    const newAscending = query.direction !== "desc";

    activeTabIndex.value = newTabIndex;
    searchQuery.value = newSearch;
    sortBy.value = newSortBy;
    isAscending.value = newAscending;

    fetchProjects(buildFetchParams({ page: newPage }));
  }
);

function onDeleteRequest({ projectId, projectName }) {
  deleteTargetId.value = projectId;
  deleteTargetName.value = projectName ?? "this project";
  showDeleteModal.value = true;
}

function onSyncDone() {
  fetchAndUpdateUrl({ page: Number(route.query.page) || 1 });
}

function onProjectAdded(alertData) {
  showAddModal.value = false;
  if (alertData) {
    pageAlert.value = alertData;
  }
  onSyncDone();
}

function onDeleteDone() {
  showDeleteModal.value = false;
  fetchAndUpdateUrl({ page: Number(route.query.page) || 1 });
}
</script>

<template>
  <PageShell>
    <div class="mx-auto w-full max-w-7xl">
      <GlAlert v-if="pageAlert" :variant="pageAlert.type" class="mb-4" @dismiss="pageAlert = null">
        {{ pageAlert.message }}
      </GlAlert>

      <ProjectsToolbar
        v-model:search-query="searchQuery"
        :tabs="tabs"
        :active-tab-index="activeTabIndex"
        :is-admin="isAdmin"
        :sort-options="SORT_OPTIONS"
        :sort-by="sortBy"
        :is-ascending="isAscending"
        @tab-change="onTabChange"
        @search="onSearch"
        @new-project="showAddModal = true"
        @sort-by-change="onSortByChange"
        @sort-direction-change="onSortDirectionChange"
      />

      <ProjectsList
        :projects="projects"
        :pagination="pagination"
        :loading="loading"
        :error="error"
        :is-admin="isAdmin"
        @dismiss-error="clearError"
        @paginate="onPaginate"
        @delete-request="onDeleteRequest"
        @sync-done="onSyncDone"
      />
    </div>

    <Teleport to="body">
      <AddProjectModal v-if="isAdmin && showAddModal" @close="showAddModal = false" @done="onProjectAdded" />
      <DeleteProjectModal
        v-if="isAdmin && showDeleteModal && deleteTargetId"
        :project-id="deleteTargetId"
        :project-name="deleteTargetName"
        @close="showDeleteModal = false"
        @done="onDeleteDone"
      />
    </Teleport>
  </PageShell>
</template>
